import { useEffect, useState } from 'react';
import { api } from '../../lib/api';

type Props = {
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
};

type SettingsBag = {
  bankBalance?: number;
  monthlyRecurringExpenses?: number;
  dsoTargetDays?: number;
  dsoIndustryDays?: number;
};

export function TesoreriaSettings({ onToast, onError }: Props) {
  const [s, setS] = useState<SettingsBag>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api<{ settings: Record<string, unknown> }>('/admin/settings')
      .then((r) => {
        const g = r.settings;
        setS({
          bankBalance: toNum(g.bankBalance) ?? 0,
          monthlyRecurringExpenses: toNum(g.monthlyRecurringExpenses) ?? 0,
          dsoTargetDays: toNum(g.dsoTargetDays) ?? 30,
          dsoIndustryDays: toNum(g.dsoIndustryDays) ?? 32,
        });
      })
      .catch(onError);
  }, [onError]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/admin/settings', { method: 'PATCH', body: s });
      onToast('Settings de Tesorería guardadas', 'ok');
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="adm-section" onSubmit={save}>
      <div className="stitle">Tesorería · settings</div>
      <h2>Parámetros del módulo Tesorería</h2>
      <div className="row2">
        <div className="field">
          <label>Saldo banco inicial (€)</label>
          <input
            type="number"
            step="0.01"
            value={s.bankBalance ?? 0}
            onChange={(e) => setS({ ...s, bankBalance: Number.parseFloat(e.target.value) || 0 })}
          />
          <div className="hint">
            punto de partida (snapshot). Los movimientos manuales de Tesorería se suman/restan sobre este saldo.
          </div>
        </div>
        <div className="field">
          <label>Gasto recurrente mensual (€)</label>
          <input
            type="number"
            step="0.01"
            min={0}
            value={s.monthlyRecurringExpenses ?? 0}
            onChange={(e) =>
              setS({ ...s, monthlyRecurringExpenses: Number.parseFloat(e.target.value) || 0 })
            }
          />
          <div className="hint">para calcular runway y previsión de tesorería</div>
        </div>
        <div className="field">
          <label>DSO objetivo (días)</label>
          <input
            type="number"
            min={0}
            max={365}
            value={s.dsoTargetDays ?? 30}
            onChange={(e) => setS({ ...s, dsoTargetDays: Number.parseInt(e.target.value, 10) || 0 })}
          />
        </div>
        <div className="field">
          <label>DSO industria (días)</label>
          <input
            type="number"
            min={0}
            max={365}
            value={s.dsoIndustryDays ?? 32}
            onChange={(e) =>
              setS({ ...s, dsoIndustryDays: Number.parseInt(e.target.value, 10) || 0 })
            }
          />
          <div className="hint">benchmark de referencia mostrado en Tesorería</div>
        </div>
      </div>
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
        <button type="submit" className="btn primary" disabled={busy}>
          Guardar
        </button>
      </div>
    </form>
  );
}

function toNum(v: unknown): number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number.parseFloat(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}
